import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle } from '@fortawesome/free-solid-svg-icons';
import HeaderImage from "../assets/all-pages.webp"
import SEO from '../components/SEO';

// Styled components for the service page
const PageWrapper = styled.div`
  padding-top: 0px; // Space for the fixed header
`;

const HeroSection = styled.section`
  height: 50vh;
  background: linear-gradient(rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.7)), url(${props => props.backgroundImage});
  background-size: cover;
  background-position: center;
  display: flex;
  align-items: center;
  position: relative;
  overflow: hidden;
  padding-top: 70px;
  color: #fff;
  
  @media (max-width: 768px) {
    height: auto;
    min-height: 40vh;
    padding: 80px 0 40px;
  }
  
  @media (max-width: 576px) {
    min-height: 30vh;
    padding: 70px 0 30px;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  
  @media (max-width: 576px) {
    padding: 0 15px;
  }
`;

const HeroTitle = styled(motion.h1)`
  font-size: 48px;
  font-weight: 700;
  margin-bottom: 20px;
  
  span {
    color: #ff6e6e;
  }
  
  @media (max-width: 768px) {
    font-size: 36px;
  }
  
  @media (max-width: 576px) {
    font-size: 28px;
    margin-bottom: 15px;
  }
`;

const HeroSubtitle = styled(motion.p)`
  font-size: 18px;
  max-width: 700px;
  margin: 0 auto 30px;
  opacity: 0.9;
  
  @media (max-width: 768px) {
    font-size: 16px;
    max-width: 90%;
  }
  
  @media (max-width: 576px) {
    font-size: 15px;
    margin-bottom: 20px;
  }
`;

const Section = styled.section`
  padding: 80px 0;
  background-color: ${props => props.bgColor || '#fff'};
  
  @media (max-width: 768px) {
    padding: 60px 0;
  }
  
  @media (max-width: 576px) {
    padding: 40px 0;
  }
`;

const SectionTitle = styled.div`
  text-align: center;
  margin-bottom: 60px;
  
  @media (max-width: 768px) {
    margin-bottom: 40px;
  }
  
  @media (max-width: 576px) {
    margin-bottom: 30px;
  }
`;

const SubTitle = styled(motion.span)`
  display: inline-block;
  font-size: 16px;
  font-weight: 500;
  color: #ff6e6e;
  text-transform: uppercase;
  letter-spacing: 2px;
  margin-bottom: 15px;
  position: relative;
  
  &:before, &:after {
    content: '';
    position: absolute;
    top: 50%;
    width: 30px;
    height: 1px;
    background-color: #ff6e6e;
  }
  
  &:before {
    left: -40px;
  }
  
  &:after {
    right: -40px;
  }
  
  @media (max-width: 576px) {
    font-size: 14px;
    letter-spacing: 1px;
    
    &:before, &:after {
      width: 20px;
    }
    
    &:before {
      left: -25px;
    }
    
    &:after {
      right: -25px;
    }
  }
`;

const Title = styled(motion.h2)`
  font-size: 36px;
  font-weight: 700;
  color: #333;
  margin-bottom: 20px;
  
  span {
    color: #ff6e6e;
  }
  
  @media (max-width: 768px) {
    font-size: 28px;
  }
  
  @media (max-width: 576px) {
    font-size: 24px;
    margin-bottom: 15px;
  }
`;

const Description = styled(motion.p)`
  font-size: 18px;
  color: #666;
  max-width: 100%;
  text-align: justify;
  margin: 10px auto;
  line-height: 1.6;
  font-weight: 600;
  
  @media (max-width: 768px) {
    font-size: 16px;
    line-height: 1.5;
  }
  
  @media (max-width: 576px) {
    font-size: 15px;
    line-height: 1.4;
    font-weight: 500;
  }
`;

const ContentRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  text-align: justify;
  margin: 40px 0;
  
  @media (max-width: 992px) {
    flex-direction: ${props => props.reverse ? 'column-reverse' : 'column'};
    margin: 30px 0;
  }
  
  @media (max-width: 576px) {
    margin: 20px 0;
  }
`;

const ContentCol = styled.div`
  flex: 1;
  padding: 20px;
  
  @media (max-width: 992px) {
    flex: 100%;
    padding: 15px;
  }
  
  @media (max-width: 576px) {
    padding: 10px 0;
  }
`;

const ImageWrapper = styled(motion.div)`
  border-radius: 8px;
  overflow: hidden;
  filter: drop-shadow(10px 10px 4px rgba(0, 0, 0, 0.6));
  
  img {
    width: 100%;
    height: auto;
    display: block;
  }
  
  @media (max-width: 576px) {
    margin: 10px 0;
    filter: drop-shadow(10px 10px 4px rgba(0, 0, 0, 0.6));
  }
`;

const FeatureList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 30px 0;
  
  @media (max-width: 576px) {
    margin: 20px 0;
  }
`;

const FeatureItem = styled(motion.li)`
  display: flex;
  align-items: flex-start;
  margin-bottom: 15px;
  font-size: 16px;
  color: #555;
  
  svg {
    color: #ff6e6e;
    margin-right: 10px;
    margin-top: 4px;
  }
  
  @media (max-width: 576px) {
    font-size: 14px;
    margin-bottom: 12px;
    
    svg {
      margin-top: 2px;
    }
  }
`;

const CTASection = styled.section`
  background-color: #ff6e6e;
  padding: 80px 0;
  text-align: center;
  color: #fff;
  
  @media (max-width: 768px) {
    padding: 60px 0;
  }
  
  @media (max-width: 576px) {
    padding: 40px 0;
  }
`;

const CTATitle = styled(motion.h2)`
  font-size: 36px;
  font-weight: 700;
  margin-bottom: 20px;
  
  @media (max-width: 768px) {
    font-size: 28px;
  }
  
  @media (max-width: 576px) {
    font-size: 24px;
    margin-bottom: 15px;
  }
`;

const CTADescription = styled(motion.p)`
  font-size: 18px;
  max-width: 700px;
  margin: 0 auto 30px;
  opacity: 0.9;
  
  @media (max-width: 768px) {
    font-size: 16px;
    max-width: 90%;
  }
  
  @media (max-width: 576px) {
    font-size: 15px;
    margin-bottom: 20px;
  }
`;

const CTAButton = styled(motion.a)`
  display: inline-block;
  background-color: #fff;
  color: #ff6e6e;
  font-weight: 600;
  padding: 15px 30px;
  border-radius: 50px;
  text-decoration: none;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: #333;
    color: #fff;
    transform: translateY(-3px);
  }
  
  @media (max-width: 768px) {
    padding: 12px 25px;
    font-size: 15px;
  }
  
  @media (max-width: 576px) {
    padding: 10px 20px;
    font-size: 14px;
    display: block;
    max-width: 200px;
    margin: 0 auto;
  }
`;

// Service Page Component
const ServicePage = ({ 
  icon, 
  title, 
  subtitle, 
  description, 
  features, 
  imagePath,
  additionalContent,
  featuresDesciption,
  metaTitle,
  metaDescription,
}) => {
  return (
    <PageWrapper>
      <SEO 
        title={metaTitle} 
        description={metaDescription}
      />
      <HeroSection backgroundImage={HeaderImage}>
        <Container>
          <HeroTitle
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            {title}
          </HeroTitle>
          <HeroSubtitle
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            {subtitle}
          </HeroSubtitle>
        </Container>
      </HeroSection>
      
      <Section>
        <Container>
          <SectionTitle>
            <SubTitle
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              OVERVIEW
            </SubTitle>
            <Title
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              About Our <span>{title}</span> Service
            </Title>
            {description.map((desc, index) => {
              return <Description
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              {desc}
            </Description>
            })}
          </SectionTitle>
          
          <ContentRow>
            <ContentCol>
              <motion.h3 
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                style={{ fontSize: '24px', fontWeight: '600', marginBottom: '20px', color: '#333' }}
              >
                Why Choose Our {title} Service?
              </motion.h3>
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.1 }}
                style={{ fontSize: '16px', color: '#666', lineHeight: '1.6', marginBottom: '20px' }}
              >
                {featuresDesciption || 'We provide comprehensive {title.toLowerCase()} solutions tailored to your specific business needs. Our team of experts ensures that you receive the highest quality service with attention to detail.'}
              </motion.p>
              
              <FeatureList>
                {features.map((feature, index) => (
                  <FeatureItem
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.1 * index }}
                  >
                    <FontAwesomeIcon icon={faCheckCircle} />
                    {feature}
                  </FeatureItem>
                ))}
              </FeatureList>
            </ContentCol>
            <ContentCol>
              <ImageWrapper
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
              >
                <img src={imagePath || '/images/hero-bg.jpg'} alt={title} />
              </ImageWrapper>
            </ContentCol>
          </ContentRow>
        </Container>
      </Section>
      
      {additionalContent && (
        <Section bgColor="#f9f9f9">
          <Container>
            {additionalContent}
          </Container>
        </Section>
      )}
      
      <CTASection>
        <Container>
          <CTATitle
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            Ready to Get Started?
          </CTATitle>
          <CTADescription
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            Contact us today to discuss how our {title.toLowerCase()} services can help your business grow.
          </CTADescription>
          <CTAButton
            href="/contact-us"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            Contact Us Now
          </CTAButton>
        </Container>
      </CTASection>
    </PageWrapper>
  );
};

export default ServicePage;